import { useState } from "react"
import { enqueueSnackbar } from "notistack"

import { useResourceBookingDraft } from "../ResourceBookingDraftContext"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { isInPast } from "../calendar/helpers"
import type { NullableCalendarEvent } from "../calendar/fullCalendarTypes"

import type { IsResourceAvailableFn } from "./timelineHooks"

export type TimeChangeDialogState = Readonly<{
    resourceId: string
    newStart: string
    newEnd: string
}>

type UseTimeChangeDialogArgs = Readonly<{
    handleChangeLocalEvent: (event: NullableCalendarEvent) => void
    isResourceAvailable: IsResourceAvailableFn
}>

export function useTimeChangeDialog({ handleChangeLocalEvent, isResourceAvailable }: UseTimeChangeDialogArgs) {
    const { l10n } = useAppLocalization()
    const { draftEvent: localEvent } = useResourceBookingDraft()
    const [timeChangeDialogState, setTimeChangeDialogState] = useState<TimeChangeDialogState | null>(null)

    const handleKeepOriginalTime = () => {
        if (!timeChangeDialogState || !localEvent) {
            setTimeChangeDialogState(null)
            return
        }

        if (isInPast(localEvent.start) || isInPast(localEvent.end)) {
            enqueueSnackbar({
                variant: "warning",
                message: l10n.getString("create-order-past-booking-attempt-message"),
            })
            return
        }

        const { resourceId } = timeChangeDialogState
        const originalStart = localEvent.start
        const originalEnd = localEvent.end

        const updatedResourceIds = localEvent.resourceIds.includes(resourceId)
            ? localEvent.resourceIds
            : [...localEvent.resourceIds, resourceId]

        if (!isResourceAvailable(originalStart, originalEnd, updatedResourceIds, localEvent.id)) {
            enqueueSnackbar({
                variant: "warning",
                message: l10n.getString("create-order-timeline-error-busy-or-overlap"),
            })
            setTimeChangeDialogState(null)
            return
        }

        handleChangeLocalEvent({
            ...localEvent,
            start: originalStart,
            end: originalEnd,
            resourceIds: updatedResourceIds,
        })

        setTimeChangeDialogState(null)
    }

    const handleConfirmTimeChange = () => {
        if (!timeChangeDialogState || !localEvent) {
            setTimeChangeDialogState(null)
            return
        }

        const { resourceId, newStart, newEnd } = timeChangeDialogState

        const updatedResourceIds = localEvent.resourceIds.includes(resourceId)
            ? localEvent.resourceIds
            : [...localEvent.resourceIds, resourceId]

        if (!isResourceAvailable(newStart, newEnd, updatedResourceIds, localEvent.id)) {
            enqueueSnackbar({
                variant: "warning",
                message: l10n.getString("create-order-timeline-error-busy-or-overlap"),
            })
            setTimeChangeDialogState(null)
            return
        }

        handleChangeLocalEvent({
            ...localEvent,
            start: newStart,
            end: newEnd,
            resourceIds: updatedResourceIds,
        })

        setTimeChangeDialogState(null)
    }

    return {
        timeChangeDialogState,
        setTimeChangeDialogState,
        handleKeepOriginalTime,
        handleConfirmTimeChange,
    }
}

